import { useState, createContext, useContext, useEffect, useLayoutEffect, useMemo, useCallback } from "react";

// ===================== React Cookies ======================== //
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { superAdmin } from "../routes/routesDefined";
import SuccessModal from "../components/SuccessModal";
import SubscriptionPlansModal from "../components/SubscriptionPlansModal";
import ChangeCardModal from "../components/ChangeCardModal";
import ApiServices from "../api/services";
import { attachIconData } from "../utils/pdfIcons";

const LOOKUPS_CACHE_KEY = "feelingsReasonsLookups";
// URL -> base64 cache so unchanged icons are never re-downloaded across logins.
const ICON_CACHE_KEY = "feelingsReasonsIconCache";

const readIconCache = () => {
  try {
    return JSON.parse(localStorage.getItem(ICON_CACHE_KEY)) || {};
  } catch {
    return {};
  }
};

const LocalContext = createContext();

export const useLoggedContext = () => {
  return useContext(LocalContext);
};

export const LocalProvider = (props) => {
  const [isLoggedin, setIsLoggedin] = useState(null);
  const [employeesListing, setEmpoyeesListing] = useState();
  const [showSidebar, setShowSidebar] = useState("");
  const [cookies, setCookies] = useCookies("token", "");
  const [sessionExpire, setSessionExpire] = useState(false);
  const [adminRole, setAdminRole] = useState("");
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [showBackDrop, setShowBackDrop] = useState(false);
  const [openModal, setOpenModal] = useState({ open: false, message: "", title: "" });
  const [user, setUser] = useState(() => JSON.parse(localStorage.getItem("userData")) || {});
  const [profileData, setProfileData] = useState([]);

  const [openSubscriptionPlansModal, setOpenSubscriptionPlansModal] = useState({ open: user?.user?.subscribed === 0 ? true : false });
  const [subPlanId, setSubPlanId] = useState("");

  // ===================== Dynamic feelings/reasons lookups ===================== //
  const [lookups, setLookups] = useState(() => {
    const empty = { polarities: [], feelings: [], reasons: [] };
    try {
      const cached = JSON.parse(localStorage.getItem(LOOKUPS_CACHE_KEY));
      // Only trust the cache if it has the expected array shape (guards against
      // any stale/corrupted value left in localStorage).
      if (cached && Array.isArray(cached.feelings) && Array.isArray(cached.reasons)) {
        return { polarities: Array.isArray(cached.polarities) ? cached.polarities : [], feelings: cached.feelings, reasons: cached.reasons };
      }
      return empty;
    } catch {
      return empty;
    }
  });

  // Always work with arrays, even if state somehow holds an unexpected shape.
  const lookupFeelings = Array.isArray(lookups.feelings) ? lookups.feelings : [];
  const lookupReasons = Array.isArray(lookups.reasons) ? lookups.reasons : [];
  const lookupPolarities = Array.isArray(lookups.polarities) ? lookups.polarities : [];

  const fetchLookups = useCallback(() => {
    ApiServices.lookups
      .getLookups()
      .then(async ({ data }) => {
        if (data?.code === 200 && data?.data) {
          // Resolve icons to base64, reusing the URL-keyed cache so only new or
          // changed icon URLs are fetched. The cache is persisted and reused
          // across logins, so unchanged icons are downloaded only once ever.
          const iconCache = readIconCache();
          const feelingsResult = await attachIconData(data.data.feelings || [], iconCache);
          const reasonsResult = await attachIconData(data.data.reasons || [], {
            ...iconCache,
            ...feelingsResult.cache,
          });

          const next = {
            polarities: data.data.polarities || [],
            feelings: feelingsResult.items,
            reasons: reasonsResult.items,
          };
          setLookups(next);
          localStorage.setItem(LOOKUPS_CACHE_KEY, JSON.stringify(next));
          // Merged, pruned cache (feelings ∪ reasons URLs currently in use).
          localStorage.setItem(ICON_CACHE_KEY, JSON.stringify({ ...feelingsResult.cache, ...reasonsResult.cache }));
        }
      })
      .catch(() => {});
  }, []);

  // Load lookups once the user is authenticated.
  useEffect(() => {
    if (isLoggedin) {
      fetchLookups();
    }
  }, [isLoggedin, fetchLookups]);

  // Derived maps + helpers
  const feelingsById = useMemo(() => {
    const map = {};
    lookupFeelings.forEach((f) => {
      map[f.id] = f;
    });
    return map;
  }, [lookups]);

  const reasonsById = useMemo(() => {
    const map = {};
    lookupReasons.forEach((r) => {
      map[r.id] = r;
    });
    return map;
  }, [lookups]);

  const feelingsByCode = useMemo(() => {
    const map = {};
    lookupFeelings.forEach((f) => {
      map[f.code] = f;
    });
    return map;
  }, [lookups]);

  const reasonsByCode = useMemo(() => {
    const map = {};
    lookupReasons.forEach((r) => {
      map[r.code] = r;
    });
    return map;
  }, [lookups]);

  // Feelings filtered by polarity ("healthy" | "unhealthy"), preserving sort_order.
  const feelingsByPolarity = useCallback(
    (polarity) => lookupFeelings.filter((f) => f.polarity === polarity),
    [lookups],
  );

  // Resolve a feeling's own reasons via its reason_ids[].
  const reasonsForFeeling = useCallback(
    (feeling) => {
      if (!feeling || !Array.isArray(feeling.reason_ids)) return [];
      return feeling.reason_ids.map((id) => reasonsById[id]).filter(Boolean);
    },
    [reasonsById],
  );

  // Open Success Modal
  const openSuccessModal = ({ ...props }) => {
    setOpenModal({ ...openModal, ...props, open: true });
  };

  // Close Success Modal
  const closeSuccessModel = () => {
    setOpenModal({ ...openModal, open: false });
  };

  // Update User
  const updateUser = (newUserData) => {
    localStorage.setItem("userData", JSON.stringify(newUserData));
    setUser(newUserData);
  };

  useEffect(() => {
    let issubscribed = user?.user?.subscribed === 0 ? true : false;
    setOpenSubscriptionPlansModal({ open: issubscribed });
  }, [user]);

  useEffect(() => {
    const status = JSON.parse(localStorage.getItem("loginStatus")) ? JSON.parse(localStorage.getItem("loginStatus")) : "";
    if (status === null || status === "undefined") {
      localStorage.setItem("loginStatus", JSON.stringify(false));
    }
    setIsLoggedin(status);
    expireSession();
    const interval = setInterval(() => {
      expireSession();
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useLayoutEffect(() => {
    const superAdmin = localStorage.getItem("superAdmin");
    setAdminRole(superAdmin);
  }, []);

  const login = (role) => {
    var now = new Date().getTime();
    localStorage.setItem("setupTime", now);
    localStorage.setItem("loginStatus", JSON.stringify(true));
    setIsLoggedin(true);
  };

  const logOut = () => {
    localStorage.setItem("loginStatus", JSON.stringify(false));
    setIsLoggedin(false);
    localStorage.clear();
    setCookies("token", "");
    setAdminRole(false);
  };

  // ======================= Session Expire Function ====================== //
  const expireSession = () => {
    var hours = 2;
    var now = new Date().getTime();
    var setupTime = localStorage.getItem("setupTime");
    if (setupTime != null) {
      if (now - setupTime > hours * 60 * 60 * 1000) {
        setSessionExpire(true);
      }
    }
  };

  const adminType = (state) => {
    localStorage.setItem("superAdmin", true);
    setAdminRole(state);
  };

  return (
    <LocalContext.Provider
      value={{
        isLoggedin,
        login,
        logOut,
        showSidebar,
        setShowSidebar,
        cookies,
        setCookies,
        sessionExpire,
        setSessionExpire,
        adminRole,
        openModal,
        setOpenModal,
        openSuccessModal,
        closeSuccessModel,
        showBackDrop,
        adminType,
        setShowBackDrop,
        employeesListing,
        btnLoading,
        setBtnLoading,
        loading,
        setLoading,
        user,
        updateUser,
        subPlanId,
        setOpenSubscriptionPlansModal,
        profileData,
        setProfileData,
        // Dynamic feelings/reasons lookups
        lookups,
        feelings: lookupFeelings,
        reasons: lookupReasons,
        polarities: lookupPolarities,
        feelingsById,
        reasonsById,
        feelingsByCode,
        reasonsByCode,
        feelingsByPolarity,
        reasonsForFeeling,
        refreshLookups: fetchLookups
      }}
    >
      {props.children && props.children}

      <SubscriptionPlansModal
        isOpen={openSubscriptionPlansModal.open}
        setIsOpen={(open) => setOpenSubscriptionPlansModal((prev) => ({ ...prev, open }))}
        fromProvider
        setSubPlanId={setSubPlanId}
      />

      <SuccessModal />
    </LocalContext.Provider>
  );
};
